// Chalk Explainer Ad's "Script & board" (live's `script-section`, `clip-header`,
// `script-line-details`, `spoken-line-row`, `board-parts-editor`, `board-part-row`,
// `end-card-fields` and `chalk-color-dot`; not captured, built from live's code, spec
// G14-chalk-explainer-ad PR d1): the ten lines under their clips' headers, each with what the
// narrator says and what the hand writes, then the end card's texts.
import { ActionIcon, Button, Group, Loader, Select, Stack, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { Plus, WandSparkles, X } from "lucide-react";

import type {
  ChalkBoardPart,
  ChalkColor,
  ChalkEndCard,
  ChalkScript,
  ChalkScriptLine,
  ChalkTimingPlan,
} from "../../../api/generated/model";
import { GENERATE_SCRIPT as GENERATE_SCRIPT_LABEL } from "../../../platform/scripts/ScriptActions";
import { FieldLabel } from "../../../ui/FieldLabel";
import {
  ADD_PART,
  CHALK_COLORS,
  COLOR_LABELS,
  EMPTY_SCRIPT_HINT,
  HEADLINE_LABEL,
  HEADLINE_PLACEHOLDER,
  MAX_BOARD_PARTS,
  PART_ICON_PLACEHOLDER,
  PART_TEXT_PLACEHOLDER,
  PRODUCT_CLOSE_NOTE,
  REMOVE_PART,
  ROLE_LABELS,
  SCRIPT_TOOLTIP,
  SPOKEN_LABEL,
  SPOKEN_PLACEHOLDER,
  SUPPORT_LINE_LABEL,
  SUPPORT_LINE_PLACEHOLDER,
  clipHeaderLabel,
  clipTiming,
  endCardHeaderLabel,
  firstLineOfClip,
  lineTiming,
  lineTimingLabel,
  newPart,
  scriptMetaLabel,
  warningLine,
} from "./chalk-script";
import chalk from "./chalk.module.css";
import classes from "./podcast.module.css";

/** Live's cap on a line as typed (`MAX_SCRIPT_LINE_LENGTH`). */
const MAX_LINE_LENGTH = 500;
const COLOR_OPTIONS = CHALK_COLORS.map((value) => ({ value, label: COLOR_LABELS[value] }));

export function ChalkDot({ color }: { color: ChalkColor }) {
  return <span className={chalk.dot} data-color={color} aria-hidden />;
}

interface PartRowProps {
  part: ChalkBoardPart;
  name: string;
  disabled: boolean;
  onChange: (part: ChalkBoardPart) => void;
  onRemove: () => void;
}

/** Live's board part row: the chalk colour, the capitals (upper-cased as typed), the icon, ×. */
function PartRow({ part, name, disabled, onChange, onRemove }: PartRowProps) {
  return (
    <div className={chalk.part}>
      <Select
        size="xs"
        w={96}
        aria-label={`${name} colour`}
        data={COLOR_OPTIONS}
        value={part.color}
        allowDeselect={false}
        disabled={disabled}
        leftSection={<ChalkDot color={part.color} />}
        onChange={(next) => {
          const color = CHALK_COLORS.find((each) => each === next);
          if (color !== undefined) onChange({ ...part, color });
        }}
      />
      <TextInput
        size="xs"
        className={chalk.partText}
        aria-label={`${name} words`}
        placeholder={PART_TEXT_PLACEHOLDER}
        value={part.text}
        disabled={disabled}
        onChange={(event) => {
          const input = event.currentTarget;
          const caret = input.selectionStart;
          const text = input.value.toUpperCase();
          input.value = text;
          input.setSelectionRange(caret, caret);
          onChange({ ...part, text });
        }}
      />
      <TextInput
        size="xs"
        className={chalk.partIcon}
        aria-label={`${name} icon`}
        placeholder={PART_ICON_PLACEHOLDER}
        value={part.icon}
        disabled={disabled}
        onChange={(event) => onChange({ ...part, icon: event.currentTarget.value })}
      />
      <Tooltip label={REMOVE_PART} withArrow>
        <ActionIcon variant="subtle" size="sm" aria-label={`${REMOVE_PART}: ${name}`} disabled={disabled} onClick={onRemove}>
          <X size={14} aria-hidden />
        </ActionIcon>
      </Tooltip>
    </div>
  );
}

interface LineDetailsProps {
  line: ChalkScriptLine;
  number: number;
  timing: ChalkTimingPlan | null;
  disabled: boolean;
  onChange: (line: ChalkScriptLine) => void;
}

/** Under a line: "Says:" with its words and times; then the board's parts, or on the last line
 * live's note that the hand raises the product. */
function LineDetails({ line, number, timing, disabled, onChange }: LineDetailsProps) {
  const times = lineTiming(timing, line.id);
  const parts = line.board.parts;
  const setParts = (next: readonly ChalkBoardPart[]) => onChange({ ...line, board: { parts: [...next] } });
  return (
    <div className={chalk.details}>
      <div className={chalk.row}>
        <Text className={chalk.label}>{SPOKEN_LABEL}</Text>
        <Textarea
          className={chalk.input}
          variant="unstyled"
          autosize
          minRows={1}
          maxLength={MAX_LINE_LENGTH}
          aria-label={`Line ${number}, what the narrator says`}
          placeholder={SPOKEN_PLACEHOLDER}
          value={line.spoken}
          disabled={disabled}
          onChange={(event) => onChange({ ...line, spoken: event.currentTarget.value })}
        />
        {times === null ? null : <Text className={chalk.timing}>{lineTimingLabel(times)}</Text>}
      </div>
      {line.role === "cta" ? (
        <Text className={chalk.note}>{PRODUCT_CLOSE_NOTE}</Text>
      ) : (
        <Stack gap={0}>
          {parts.map((part, index) => (
            <PartRow
              // A part has no id of its own; its place in the line is its identity.
              key={index}
              part={part}
              name={`Line ${number}, part ${index + 1}`}
              disabled={disabled}
              onChange={(next) => setParts(parts.map((each, at) => (at === index ? next : each)))}
              onRemove={() => setParts(parts.filter((_, at) => at !== index))}
            />
          ))}
          {parts.length < MAX_BOARD_PARTS ? (
            <Button
              variant="subtle"
              size="compact-xs"
              className={chalk.add}
              leftSection={<Plus size={12} aria-hidden />}
              aria-label={`Add a part to line ${number}`}
              disabled={disabled}
              onClick={() => setParts([...parts, newPart(parts.at(-1))])}
            >
              {ADD_PART}
            </Button>
          ) : null}
        </Stack>
      )}
    </div>
  );
}

function EndCardFields(props: { endCard: ChalkEndCard; seconds: number; disabled: boolean; onChange: (endCard: ChalkEndCard) => void }) {
  const { endCard, seconds, disabled, onChange } = props;
  const fields = [
    { key: "headline", label: HEADLINE_LABEL, placeholder: HEADLINE_PLACEHOLDER },
    { key: "support_line", label: SUPPORT_LINE_LABEL, placeholder: SUPPORT_LINE_PLACEHOLDER },
  ] as const;
  return (
    <Stack gap={0}>
      <Text className={chalk.endCardHeader}>{endCardHeaderLabel(seconds)}</Text>
      {fields.map((field) => (
        <div key={field.key} className={chalk.row}>
          <Text className={chalk.label}>{field.label}</Text>
          <Textarea
            className={chalk.input}
            variant="unstyled"
            autosize
            minRows={1}
            maxLength={MAX_LINE_LENGTH}
            aria-label={`End card ${field.label.toLowerCase()}`}
            placeholder={field.placeholder}
            value={endCard[field.key]}
            disabled={disabled}
            onChange={(event) => onChange({ ...endCard, [field.key]: event.currentTarget.value })}
          />
        </div>
      ))}
    </Stack>
  );
}

export interface ChalkScriptSectionProps {
  /** The script, or none before it is generated. */
  script: ChalkScript | null;
  /** The script's timing plan as the server last read it: the clips' and lines' times. */
  timing: ChalkTimingPlan | null;
  /** A director writes: live's loader over the lines. */
  writing: boolean;
  /** Nothing changes: a director works, or the script is approved. */
  locked: boolean;
  /** What keeps the script from approval (live's error line). */
  issue: string | null;
  /** Live's warnings under it: the tries, the timing plan's, the script's problems. */
  warnings: readonly string[];
  onGenerate: () => void;
  onChange: (script: ChalkScript) => void;
}

/**
 * Live's "Script & board": its info tooltip; at the right the meta line and the wand (Generate
 * Script; live offers no pencil here). Without a script, live's hint; with one, each clip's
 * header over its first line, a row a line (its number, its role, what is written), what the
 * narrator says with its times and the board's parts under it, then the end card's headline and
 * line. The issue and the warnings follow.
 */
export function ChalkScriptSection(props: ChalkScriptSectionProps) {
  const { script, timing, writing, locked, issue, warnings, onGenerate, onChange } = props;
  const lines = script?.lines ?? [];
  const setLine = (index: number, line: ChalkScriptLine) => {
    if (script !== null) onChange({ ...script, lines: script.lines.map((each, at) => (at === index ? line : each)) });
  };
  return (
    <section aria-label="Script & board">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Script & board" info={SCRIPT_TOOLTIP} spoken />
        </Text>
        <Group gap={8} wrap="nowrap">
          {timing === null || script === null ? null : <Text className={classes.meta}>{scriptMetaLabel(timing)}</Text>}
          <Tooltip label={GENERATE_SCRIPT_LABEL} withArrow>
            <ActionIcon variant="subtle" size={36} aria-label={GENERATE_SCRIPT_LABEL} disabled={locked} onClick={onGenerate}>
              <WandSparkles size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
        </Group>
      </div>
      <div className={classes.editor} data-busy={writing || undefined}>
        {script === null ? (
          <Text className={classes.hint}>
            Press
            <WandSparkles size={14} className={classes.hintIcon} aria-label="the wand" />
            {EMPTY_SCRIPT_HINT}
          </Text>
        ) : (
          <div className={classes.lines}>
            {lines.map((line, index) => {
              const clip = firstLineOfClip(lines, index) ? clipTiming(timing, line.clip) : null;
              const role = ROLE_LABELS[line.role];
              return (
                <div key={line.id}>
                  {clip === null ? null : <Text className={chalk.clipHeader}>{clipHeaderLabel(clip)}</Text>}
                  <div className={classes.line}>
                    <span className={classes.number}>{index + 1}</span>
                    <span className={classes.beat} title={role}>
                      {role}
                    </span>
                    <Textarea
                      className={classes.lineText}
                      classNames={{ input: classes.lineInput }}
                      variant="unstyled"
                      autosize
                      minRows={1}
                      maxLength={MAX_LINE_LENGTH}
                      aria-label={`Line ${index + 1}, ${role}`}
                      value={line.text}
                      disabled={locked}
                      onChange={(event) => setLine(index, { ...line, text: event.currentTarget.value })}
                    />
                  </div>
                  <LineDetails
                    line={line}
                    number={index + 1}
                    timing={timing}
                    disabled={locked}
                    onChange={(next) => setLine(index, next)}
                  />
                </div>
              );
            })}
            {timing === null ? null : (
              <EndCardFields
                endCard={script.end_card}
                seconds={timing.end_card_s}
                disabled={locked}
                onChange={(endCard) => onChange({ ...script, end_card: endCard })}
              />
            )}
          </div>
        )}
        {writing ? (
          <div className={classes.editorLoader}>
            <Loader size="sm" aria-label="Writing the script" />
          </div>
        ) : null}
      </div>
      {issue === null ? null : <Text className={classes.issue}>{issue}</Text>}
      {warnings.length === 0 ? null : (
        <div className={chalk.warnings}>
          {warnings.map((warning) => (
            <Text key={warning}>{warningLine(warning)}</Text>
          ))}
        </div>
      )}
    </section>
  );
}
